import { useEffect, useState } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, RefreshCw, TrendingUp, TrendingDown, Sparkles, Globe,
  MousePointerClick, Eye, BarChart3, Target, Mail, Search, Activity,
  AlertTriangle, Zap,
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar, LineChart, Line,
} from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';
import { PageMeta } from '@/components/PageMeta';
import { formatApiError } from '@/components/ProtectedRoute';
import { API } from '@/lib/api';

const fmtNum = (n) => (n == null ? '—' : n.toLocaleString('fr-FR'));
const fmtPct = (n) => (n == null ? '—' : `${(n * 100).toFixed(1)}%`);
const fmtPos = (n) => (n == null ? '—' : Number(n).toFixed(1));
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR') : '—');

const StatCard = ({ icon: Icon, label, value, hint, color = 'primary' }) => (
  <Card className="p-5 rounded-2xl">
    <div className="flex items-start justify-between mb-2">
      <div className={`p-2 rounded-xl bg-${color}/10 text-${color}`}>
        <Icon className="h-5 w-5" />
      </div>
    </div>
    <div className="text-2xl font-display text-dark mb-0.5">{value}</div>
    <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
    {hint && <div className="text-[11px] text-muted-foreground mt-2">{hint}</div>}
  </Card>
);

export default function AdminSeoPage() {
  const [status, setStatus] = useState(null);
  const [overview, setOverview] = useState(null);
  const [queries, setQueries] = useState([]);
  const [quickWins, setQuickWins] = useState([]);
  const [pages, setPages] = useState([]);
  const [devices, setDevices] = useState({ devices: [], countries: [] });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [running, setRunning] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [audit, setAudit] = useState(null);
  const [ctrOpps, setCtrOpps] = useState([]);
  const [drops, setDrops] = useState([]);
  const [ctrSuggesting, setCtrSuggesting] = useState(null); // {query, variants}

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, o, q, qw, p, d, ctr, dd] = await Promise.all([
        axios.get(`${API}/admin/seo/status`).then((r) => r.data).catch(() => null),
        axios.get(`${API}/admin/seo/overview?days=28`).then((r) => r.data).catch(() => null),
        axios.get(`${API}/admin/seo/queries?limit=50`).then((r) => r.data).catch(() => ({ queries: [] })),
        axios.get(`${API}/admin/seo/quick-wins?limit=20`).then((r) => r.data).catch(() => ({ quick_wins: [] })),
        axios.get(`${API}/admin/seo/pages?limit=20`).then((r) => r.data).catch(() => ({ pages: [] })),
        axios.get(`${API}/admin/seo/devices?days=28`).then((r) => r.data).catch(() => ({ devices: [], countries: [] })),
        axios.get(`${API}/admin/seo/ctr-opportunities`).then((r) => r.data).catch(() => ({ opportunities: [] })),
        axios.get(`${API}/admin/seo/position-drops`).then((r) => r.data).catch(() => ({ drops: [] })),
      ]);
      setStatus(s);
      setOverview(o);
      setQueries((q?.queries || []));
      setQuickWins((qw?.quick_wins || []));
      setPages((p?.pages || []));
      setDevices(d || { devices: [], countries: [] });
      setCtrOpps(ctr?.opportunities || []);
      setDrops(dd?.drops || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/admin/seo/sync`);
      toast.success(`Sync OK : ${data.queries_rows} requêtes, ${data.pages_rows} pages`);
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSyncing(false);
    }
  };

  const handleWeeklyRun = async () => {
    if (!window.confirm("Lancer maintenant l'optimisation auto (sync + génération article quick-win + email récap) ?")) return;
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/admin/seo/weekly-run`);
      if (data.blog_draft_id) {
        toast.success(`Article généré : ${data.blog_title?.slice(0, 60) || ''}`);
      } else if (data.blog_error) {
        toast.warning(`Sync OK mais IA échouée : ${data.blog_error.slice(0, 100)}`);
      } else {
        toast.success(`Run OK · ${data.quick_wins_detected} quick-wins`);
      }
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRunning(false);
    }
  };

  const handleIndexationAudit = async () => {
    if (!window.confirm("Auditer l'indexation Google ? (consomme jusqu'à 30 inspections sur quota 200/jour)")) return;
    setAuditLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/seo/indexation-audit`, { sample_size: 30 });
      setAudit(data);
      toast.success(`Audit : ${data.summary.indexed} indexées · ${data.summary.not_indexed} non indexées sur ${data.summary.sample_size}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAuditLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  if (!status?.configured) {
    return (
      <AdminLayout>
        <PageMeta title="SEO Auto-Pilote" description="Tableau de bord SEO" />
        <Card className="p-8 rounded-2xl text-center" data-testid="seo-not-configured">
          <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="font-heading text-2xl text-dark mb-3">Search Console pas encore connecté</h2>
          <p className="text-muted-foreground">L'API Google Search Console n'est pas configurée sur ce serveur.</p>
        </Card>
      </AdminLayout>
    );
  }

  const totals = overview?.totals || {};
  const dailyData = (overview?.daily || []).map((d) => ({ ...d, ctr_pct: (d.ctr || 0) * 100 }));

  return (
    <AdminLayout>
      <PageMeta title="SEO Auto-Pilote" description="Tableau de bord SEO" />

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <span className="text-primary font-semibold text-xs tracking-[0.3em] uppercase">━━ SEO</span>
          <h1 className="font-heading text-4xl md:text-5xl text-dark mt-2">
            AUTO-<span className="text-gradient-primary italic font-display inline-block pr-2">PILOTE</span>
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Données réelles Google Search Console · {status.property} · Dernier sync {fmtDate(status.last_sync)}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={loadAll} disabled={loading} className="rounded-xl" data-testid="seo-refresh">
            <RefreshCw className="h-4 w-4 mr-2" /> Rafraîchir
          </Button>
          <Button variant="outline" onClick={handleSync} disabled={syncing} className="rounded-xl" data-testid="seo-sync">
            {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Activity className="h-4 w-4 mr-2" />}
            Sync GSC
          </Button>
          <Button onClick={handleWeeklyRun} disabled={running} className="bg-primary hover:bg-primary-light rounded-xl text-white" data-testid="seo-weekly-run">
            {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            Optimiser maintenant
          </Button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={MousePointerClick} label="Clics (28j)" value={fmtNum(totals.clicks)} hint="depuis Google Search" />
        <StatCard icon={Eye} label="Impressions (28j)" value={fmtNum(totals.impressions)} hint="vues dans les SERP" />
        <StatCard icon={BarChart3} label="CTR moyen" value={fmtPct(totals.ctr)} hint="clics / impressions" />
        <StatCard icon={Target} label="Position moyenne" value={fmtPos(totals.avg_position)} hint="sur l'ensemble des requêtes" />
      </div>

      {/* Trend chart */}
      <Card className="p-5 rounded-2xl mb-6" data-testid="seo-trend-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg text-dark">ÉVOLUTION SUR 28 JOURS</h2>
          <Badge variant="outline" className="text-xs">Clics + Impressions</Badge>
        </div>
        {dailyData.length === 0 ? (
          <div className="h-64 flex items-center justify-center text-sm text-muted-foreground">
            Aucune donnée. Lance un Sync GSC.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={dailyData}>
              <defs>
                <linearGradient id="grClicks" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0a3d2c" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#0a3d2c" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="grImpr" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#22c55e" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Area type="monotone" dataKey="impressions" stroke="#22c55e" fill="url(#grImpr)" strokeWidth={2} name="Impressions" />
              <Area type="monotone" dataKey="clicks" stroke="#0a3d2c" fill="url(#grClicks)" strokeWidth={2} name="Clics" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* Quick wins */}
      <Card className="p-5 rounded-2xl mb-6" data-testid="seo-quickwins-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="font-heading text-lg text-dark flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" /> QUICK WINS
            </h2>
            <p className="text-xs text-muted-foreground mt-1">Mots-clés en position 6-20 → un article ciblé peut te faire passer en page 1</p>
          </div>
          <Badge className="bg-primary/15 text-primary border-primary/30">{quickWins.length} opportunités</Badge>
        </div>
        {quickWins.length === 0 ? (
          <p className="text-sm text-muted-foreground italic py-4">Aucun quick win détecté pour le moment. Continue à publier des articles, les positions montent !</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="text-left py-2 pr-3">Mot-clé</th>
                  <th className="text-right py-2 px-3">Position</th>
                  <th className="text-right py-2 px-3">Impr.</th>
                  <th className="text-right py-2 px-3">Clics</th>
                  <th className="text-right py-2 px-3">CTR</th>
                  <th className="text-right py-2 pl-3">Score</th>
                </tr>
              </thead>
              <tbody>
                {quickWins.map((q, i) => (
                  <tr key={i} className="border-b border-border/50 hover:bg-accent/30" data-testid={`quickwin-${i}`}>
                    <td className="py-2 pr-3 font-medium text-dark">{q.query}</td>
                    <td className="py-2 px-3 text-right">
                      <Badge variant={q.position <= 10 ? 'default' : 'secondary'} className={q.position <= 10 ? 'bg-amber-500/15 text-amber-700' : ''}>
                        {fmtPos(q.position)}
                      </Badge>
                    </td>
                    <td className="py-2 px-3 text-right text-muted-foreground">{fmtNum(q.impressions)}</td>
                    <td className="py-2 px-3 text-right text-muted-foreground">{fmtNum(q.clicks)}</td>
                    <td className="py-2 px-3 text-right text-muted-foreground">{fmtPct(q.ctr)}</td>
                    <td className="py-2 pl-3 text-right font-semibold text-primary">{q.opportunity_score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* 2-column grid: Top queries / Top pages */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Card className="p-5 rounded-2xl" data-testid="seo-queries-card">
          <h2 className="font-heading text-base text-dark mb-3">TOP MOTS-CLÉS (28j)</h2>
          {queries.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">—</p>
          ) : (
            <div className="space-y-1.5 max-h-96 overflow-y-auto pr-1">
              {queries.slice(0, 30).map((q, i) => (
                <div key={i} className="flex items-center gap-2 text-xs py-1.5 border-b border-border/40 last:border-0">
                  <span className="flex-1 truncate text-dark">{q.query}</span>
                  <span className="text-muted-foreground tabular-nums w-12 text-right">{fmtNum(q.clicks)} clics</span>
                  <Badge variant="outline" className="text-[10px] tabular-nums">pos {fmtPos(q.position)}</Badge>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-5 rounded-2xl" data-testid="seo-pages-card">
          <h2 className="font-heading text-base text-dark mb-3">TOP PAGES (28j)</h2>
          {pages.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">—</p>
          ) : (
            <div className="space-y-1.5 max-h-96 overflow-y-auto pr-1">
              {pages.slice(0, 30).map((p, i) => (
                <div key={i} className="flex items-center gap-2 text-xs py-1.5 border-b border-border/40 last:border-0">
                  <span className="flex-1 truncate text-dark" title={p.page}>{p.page.replace(/^https?:\/\/[^/]+/, '')}</span>
                  <span className="text-muted-foreground tabular-nums w-12 text-right">{fmtNum(p.clicks)} clics</span>
                  <Badge variant="outline" className="text-[10px] tabular-nums">{fmtNum(p.impressions)} impr</Badge>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Devices + Countries */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Card className="p-5 rounded-2xl">
          <h2 className="font-heading text-base text-dark mb-3">RÉPARTITION APPAREILS</h2>
          {(devices.devices || []).length === 0 ? (
            <p className="text-sm text-muted-foreground italic">—</p>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={devices.devices}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="key" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="clicks" fill="#0a3d2c" name="Clics" />
                <Bar dataKey="impressions" fill="#22c55e" name="Impr." />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
        <Card className="p-5 rounded-2xl">
          <h2 className="font-heading text-base text-dark mb-3">TOP PAYS</h2>
          {(devices.countries || []).length === 0 ? (
            <p className="text-sm text-muted-foreground italic">—</p>
          ) : (
            <div className="space-y-2">
              {(devices.countries || []).slice(0, 6).map((c, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="flex-1 uppercase">{c.key}</span>
                  <span className="text-muted-foreground tabular-nums w-16 text-right">{fmtNum(c.clicks)} clics</span>
                  <span className="text-muted-foreground tabular-nums w-20 text-right">{fmtNum(c.impressions)} impr</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Indexation audit */}
      <Card className="p-5 rounded-2xl mb-6" data-testid="seo-indexation-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="font-heading text-base text-dark">AUDIT INDEXATION GOOGLE</h2>
            <p className="text-xs text-muted-foreground mt-1">Vérifie quelles pages sont effectivement indexées dans Google (échantillon de 30 URLs).</p>
          </div>
          <Button onClick={handleIndexationAudit} disabled={auditLoading} variant="outline" className="rounded-xl" data-testid="seo-audit-btn">
            {auditLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
            Lancer l'audit
          </Button>
        </div>
        {audit && (
          <div className="mt-4">
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="p-3 rounded-xl bg-emerald-500/10 text-emerald-700 text-center">
                <div className="text-2xl font-display">{audit.summary.indexed}</div>
                <div className="text-xs uppercase">Indexées</div>
              </div>
              <div className="p-3 rounded-xl bg-red-500/10 text-red-700 text-center">
                <div className="text-2xl font-display">{audit.summary.not_indexed}</div>
                <div className="text-xs uppercase">Non indexées</div>
              </div>
              <div className="p-3 rounded-xl bg-muted text-center">
                <div className="text-2xl font-display">{audit.summary.total_in_sitemap}</div>
                <div className="text-xs uppercase">Total sitemap</div>
              </div>
            </div>
            {audit.not_indexed?.length > 0 && (
              <div>
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Pages non indexées (à investiguer)</h3>
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {audit.not_indexed.map((it, i) => (
                    <div key={i} className="text-xs py-1.5 border-b border-border/40 last:border-0">
                      <div className="font-medium text-dark truncate">{it.url.replace(/^https?:\/\/[^/]+/, '') || '/'}</div>
                      <div className="text-muted-foreground flex flex-wrap gap-x-3">
                        <span>verdict: <strong>{it.verdict}</strong></span>
                        {it.coverage && <span>coverage: {it.coverage}</span>}
                        {it.indexing_state && <span>state: {it.indexing_state}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Position drops alerter */}
      {drops.length > 0 && (
        <Card className="p-5 rounded-2xl mb-6 border-red-300 bg-red-50/50" data-testid="seo-drops-card">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <h2 className="font-heading text-lg text-red-700">CHUTES DE POSITION DÉTECTÉES</h2>
            <Badge className="bg-red-600 text-white">{drops.length}</Badge>
          </div>
          <p className="text-xs text-muted-foreground mb-3">Mots-clés qui ont perdu ≥ 5 places entre les 2 derniers snapshots GSC. Investigue rapidement.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="text-left py-2 pr-3">Mot-clé</th>
                  <th className="text-right py-2 px-3">Avant</th>
                  <th className="text-right py-2 px-3">Maintenant</th>
                  <th className="text-right py-2 px-3">Δ</th>
                  <th className="text-right py-2 pl-3">Impr.</th>
                </tr>
              </thead>
              <tbody>
                {drops.slice(0, 10).map((d, i) => (
                  <tr key={i} className="border-b border-border/50">
                    <td className="py-2 pr-3 font-medium">{d.query}</td>
                    <td className="py-2 px-3 text-right text-muted-foreground">{d.prev_position}</td>
                    <td className="py-2 px-3 text-right font-semibold text-red-700">{d.new_position}</td>
                    <td className="py-2 px-3 text-right text-red-600">−{d.drop}</td>
                    <td className="py-2 pl-3 text-right text-muted-foreground">{d.impressions}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* CTR Booster */}
      {ctrOpps.length > 0 && (
        <Card className="p-5 rounded-2xl mb-6" data-testid="seo-ctr-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="font-heading text-lg text-dark flex items-center gap-2">
                <Zap className="h-5 w-5 text-amber-500" /> CTR BOOSTER
              </h2>
              <p className="text-xs text-muted-foreground mt-1">Pages bien classées (top 10) mais avec CTR sous-performant. L'IA peut suggérer un title + meta optimisés.</p>
            </div>
            <Badge className="bg-amber-500/15 text-amber-700">{ctrOpps.length} opportunités</Badge>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="text-left py-2 pr-3">Mot-clé</th>
                  <th className="text-right py-2 px-3">Pos.</th>
                  <th className="text-right py-2 px-3">CTR actuel</th>
                  <th className="text-right py-2 px-3">CTR cible</th>
                  <th className="text-right py-2 px-3">Clics potentiels</th>
                  <th className="text-right py-2 pl-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {ctrOpps.slice(0, 8).map((q, i) => (
                  <tr key={i} className="border-b border-border/50">
                    <td className="py-2 pr-3 font-medium">{q.query}</td>
                    <td className="py-2 px-3 text-right">{fmtPos(q.position)}</td>
                    <td className="py-2 px-3 text-right text-red-600">{fmtPct(q.ctr)}</td>
                    <td className="py-2 px-3 text-right text-emerald-600">{fmtPct(q.benchmark_ctr)}</td>
                    <td className="py-2 px-3 text-right font-semibold text-primary">+{q.potential_clicks}</td>
                    <td className="py-2 pl-3 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-lg h-7 text-xs"
                        disabled={ctrSuggesting?.loading}
                        onClick={async () => {
                          setCtrSuggesting({ query: q.query, loading: true });
                          try {
                            const { data } = await axios.post(`${API}/admin/seo/ctr-suggest`, {
                              query: q.query,
                              page_url: '',
                            });
                            if (data.error) {
                              toast.error(`IA : ${data.error}`);
                              setCtrSuggesting(null);
                            } else {
                              setCtrSuggesting({ query: q.query, variants: data.variants || [] });
                              toast.success('Variantes générées par Claude');
                            }
                          } catch (err) {
                            toast.error(formatApiError(err));
                            setCtrSuggesting(null);
                          }
                        }}
                        data-testid={`ctr-suggest-${i}`}
                      >
                        {ctrSuggesting?.query === q.query && ctrSuggesting?.loading
                          ? <Loader2 className="h-3 w-3 animate-spin" />
                          : <Sparkles className="h-3 w-3" />}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {ctrSuggesting?.variants && (
            <div className="mt-4 p-4 bg-primary/5 rounded-xl border border-primary/20">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-heading text-sm">3 variantes pour « {ctrSuggesting.query} »</h3>
                <Button size="sm" variant="ghost" onClick={() => setCtrSuggesting(null)}>Fermer</Button>
              </div>
              {ctrSuggesting.variants.map((v, i) => (
                <div key={i} className="mb-3 p-3 bg-white rounded-lg border">
                  <div className="text-xs uppercase text-muted-foreground mb-1">Variante {i + 1}</div>
                  <div className="font-semibold text-dark mb-1">{v.title}</div>
                  <div className="text-sm text-muted-foreground mb-1">{v.meta_description}</div>
                  <div className="text-[11px] italic text-primary">💡 {v.rationale}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Auto-pilot status */}
      <Card className="p-5 rounded-2xl bg-primary/5 border-primary/20">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-xl bg-primary text-white">
            <Sparkles className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading text-base text-dark mb-1">Optimisation hebdomadaire automatique</h3>
            <p className="text-xs text-muted-foreground">
              Tous les lundis, l'IA détecte le quick-win #1, génère un article SEO/AEO en brouillon ciblant ce mot-clé, et t'envoie un récap par email.
              <br />Dernier run : <strong>{fmtDate(status.last_weekly_run)}</strong>
            </p>
            <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
              <Mail className="h-3.5 w-3.5" /> Récap envoyé à crossfit.saintchamond@gmail.com
            </div>
          </div>
        </div>
      </Card>
    </AdminLayout>
  );
}
